import { AxiosResponse } from 'axios'
import {
  ChangeWorksitePayload,
  CreateEmployeeApiType,
  EmployeeIdParams,
  EmployeeSuccessPayload,
  MyCommuterDetailsParams,
  MyCommutersParams,
  MyCommuterTransactionsParams,
  CreateFleetCardPayload,
  ReplaceFleetCardPayload,
  RequestTransitCardPayload,
  ToggleFleetCardPayload,
} from 'redux/employee/employeeTypes'
import Request from '../config/services/Request'
import {
  RosterListParams,
  rosterListPath,
  rosterSummaryPath,
} from './rosterQuery'

const getAllEmployees = (): Promise<AxiosResponse> => {
  return Request.get('/hr/profile/users/')
}

type updateEmployeeParams = EmployeeSuccessPayload

const updateEmployee = (data: updateEmployeeParams): Promise<AxiosResponse> => {
  return Request.put(`/hr/profile/users/${data.id}/`, data)
}

const deleteEmployee = (id: string): Promise<AxiosResponse> => {
  return Request.del(`/hr/profile/users/${id}/`)
}

const createEmployee = (
  data: CreateEmployeeApiType
): Promise<AxiosResponse> => {
  return Request.post('/hr/profile/users/', data)
}

const inviteEmployees = (emails: string[]): Promise<AxiosResponse>[] => {
  const invitedEmployees: Promise<AxiosResponse>[] = emails.map((email) => {
    return Request.post('/hr/profile/users/', {
      email,
      groups: ['HRusers'],
      worksiteId: null,
    })
  })
  return invitedEmployees
}

const resendEmployeeInvite = (employeeId: string): Promise<AxiosResponse> => {
  return Request.post(`/hr/profile/users/${employeeId}/resend_invite/`, {
    id: employeeId,
  })
}

const getCommuters = (commuterId?: string): Promise<AxiosResponse> => {
  return Request.get(
    `/hr/profile/my_employees/${
      commuterId && typeof commuterId === 'string' ? commuterId : ''
    }`
  )
}

const getMyCommuters = (params: MyCommutersParams): Promise<AxiosResponse> => {
  return Request.get(
    `/hr/profile/my_commuters/?archived=false${
      params.segment ? `&segments=${params.segment}` : ''
    }${params.segment && params.csv ? '&' : ''}${
      params.csv ? `&format=csv` : ''
    }`,
    params.csv ? true : false
  )
}

const getMyCommuterDetails = (
  params: MyCommuterDetailsParams
): Promise<AxiosResponse> => {
  return Request.get(
    `/hr/profile/my_commuters/${params.id}/?date__gte=${params.startDate}&date__lt=${params.endDate}`
  )
}

const getMyCommuterTransactions = (
  params: MyCommuterTransactionsParams
): Promise<AxiosResponse> => {
  return Request.get(
    `/hr/profile/my_commuters/${params.id}/transactions/?date__gte=${
      params.startDate || ''
    }&date__lte=${params.endDate || ''}`
  )
}

const getArchivedEmployees = (): Promise<AxiosResponse> => {
  return Request.get(`/hr/profile/my_employees/?archived=true`)
}

const archiveEmployee = (params: EmployeeIdParams): Promise<AxiosResponse> => {
  return Request.del(`/hr/profile/my_employees/${params.employeeId}/`)
}

const reactivateEmployee = (
  params: EmployeeIdParams
): Promise<AxiosResponse> => {
  return Request.post(
    `/hr/profile/my_employees/${params.employeeId}/reactivate/`,
    {}
  )
}

const getDeclinedEmployees = (): Promise<AxiosResponse> => {
  return Request.get(`/hr/profile/my_declined_employees/`)
}

const getRosterPage = (params: RosterListParams): Promise<AxiosResponse> => {
  return Request.get(rosterListPath(params))
}

const getRosterSummary = (params: {
  segment?: string
}): Promise<AxiosResponse> => {
  return Request.get(rosterSummaryPath(params.segment))
}

const searchEmployees = (queryText: string): Promise<AxiosResponse> => {
  const query = new URLSearchParams()
  query.set('q', queryText)
  return Request.get(`/hr/profile/my_employees/search/?${query.toString()}`)
}

const changeEmployeeWorksite = (
  params: ChangeWorksitePayload
): Promise<AxiosResponse> => {
  return Request.post(
    `/hr/profile/my_employees/${params.employeeId}/change_worksite/`,
    {
      worksiteId: params.worksiteId,
    }
  )
}

const requestTransitCard = (
  params: RequestTransitCardPayload
): Promise<AxiosResponse> => {
  return Request.post(
    `/hr/profile/my_commuters/${params.employeeId}/request_transit_card/`,
    {
      transitAgency: params.transitAgencyId,
      addFunds: params.addFunds,
      amount: params.amount ?? null,
    }
  )
}

const activateFleetCard = (
  params: ToggleFleetCardPayload
): Promise<AxiosResponse> => {
  return Request.post(
    `/hr/core/cards/${params.cardInternalId}/activate/`,
    null
  )
}

const deactivateFleetCard = (
  params: ToggleFleetCardPayload
): Promise<AxiosResponse> => {
  return Request.post(
    `/hr/core/cards/${params.cardInternalId}/deactivate/`,
    null
  )
}

const replaceFleetCard = (
  params: ReplaceFleetCardPayload
): Promise<AxiosResponse> => {
  return Request.post(`/hr/core/cards/${params.cardInternalId}/replace/`, {
    replacementReason: params.replacementReason,
    ...(params.overrideShippingAddress
      ? { overrideShippingAddress: params.overrideShippingAddress }
      : {}),
  })
}

const createFleetCard = (
  params: CreateFleetCardPayload
): Promise<AxiosResponse> => {
  return Request.post(`/hr/core/cards/create_card/`, {
    employeeId: params.employeeId,
    cardType: params.cardType,
    ...(params.overrideShippingAddress
      ? { overrideShippingAddress: params.overrideShippingAddress }
      : {}),
  })
}

export default {
  getAllEmployees,
  updateEmployee,
  deleteEmployee,
  createEmployee,
  inviteEmployees,
  getCommuters,
  getMyCommuters,
  getMyCommuterDetails,
  getMyCommuterTransactions,
  resendEmployeeInvite,
  getArchivedEmployees,
  archiveEmployee,
  reactivateEmployee,
  getDeclinedEmployees,
  getRosterPage,
  getRosterSummary,
  searchEmployees,
  changeEmployeeWorksite,
  requestTransitCard,
  activateFleetCard,
  deactivateFleetCard,
  replaceFleetCard,
  createFleetCard,
}
